import { todayLocal } from '@/lib/dates'
import { useState } from 'react'
import { useParams } from 'react-router'
import { CircleAlert, Info } from 'lucide-react'
import {
  CurrencyValue, DateValue, EmptyState, FormDialog, PageHeader, StatusBadge, EmployeePicker } from '@/components/common'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { useBreadcrumb } from '@/hooks/useBreadcrumb'
import { toApiProblem } from '@/lib/api/client'
import { Select } from '@/components/ui/select'
import { DatePicker } from '@/components/ui/date-picker'
import {
  useAsset, useAssetAssignments, useAssetMaintenance, useAssetMutations, useLicenceCheck,
} from '../api'
import {
  ASSET_STATUS_LABELS, ASSIGNMENT_STATUS_LABELS, CONDITION_LABELS, DISPOSAL_METHOD_LABELS,
  MAINTENANCE_KIND_LABELS,
} from '../types'
import type {
  AssetCondition, AssetStatus, AssignmentStatus, MaintenanceKind,
} from '../types'

const STATUS_TONE: Record<AssetStatus, 'neutral' | 'info' | 'success' | 'warning' | 'danger'> = {
  IN_STOCK: 'success',
  ASSIGNED: 'info',
  IN_MAINTENANCE: 'warning',
  LOST: 'danger',
  DISPOSED: 'neutral',
}

/** One asset: where it is, who has had it, what has been done to it, what it is worth. */
export function AssetDetailPage() {
  useBreadcrumb()
  const { id } = useParams<{ id: string }>()
  const asset = useAsset(id)
  const assignments = useAssetAssignments(id)
  const maintenance = useAssetMaintenance(id)
  const {
    assign, returnAsset, markLost, recordMaintenance, dispose,
  } = useAssetMutations()

  const [assignDraft, setAssignDraft] = useState<{
    employeeId: string; assignedOn: string; conditionOut: AssetCondition
    expectedReturnOn: string; purpose: string
  } | null>(null)
  const [returnDraft, setReturnDraft] = useState<{
    id: string; returnedOn: string; conditionIn: string; note: string
  } | null>(null)
  const [maintenanceDraft, setMaintenanceDraft] = useState<{
    kind: MaintenanceKind; scheduledOn: string; performedOn: string; nextDueOn: string
    vendor: string; cost: string; currency: string; description: string
    tookOutOfService: boolean
  } | null>(null)
  const [disposeDraft, setDisposeDraft] = useState<{
    disposedOn: string; method: string; proceeds: string; reason: string
  } | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [dialogError, setDialogError] = useState<string | null>(null)

  const licence = useLicenceCheck(id, assignDraft?.employeeId)

  const act = (work: () => Promise<unknown>, fallback: string, inDialog = false) => {
    void (async () => {
      setError(null)
      setDialogError(null)
      try {
        await work()
      } catch (caught) {
        const message = toApiProblem(caught)?.detail ?? fallback
        if (inDialog) setDialogError(message)
        else setError(message)
      }
    })()
  }

  if (asset.isPending) {
    return <Skeleton className="h-64" />
  }
  if (!asset.isSuccess) {
    return <EmptyState title="That asset could not be loaded" description="It may have been removed." />
  }

  const open = assignments.data?.find((row) => row.status === 'ASSIGNED')

  return (
    <div className="space-y-4">
      <PageHeader
        title={`${asset.data.assetTag} · ${asset.data.name}`}
        subtitle={[asset.data.categoryName, asset.data.serialNumber, asset.data.model]
          .filter(Boolean).join(' · ')}
        actions={
          <div className="flex flex-wrap gap-2">
            {!open && asset.data.status !== 'DISPOSED' && (
              <Button
                type="button"
                onClick={() => {
                  setError(null)
                  setAssignDraft({
                    employeeId: '', assignedOn: todayLocal(),
                    conditionOut: asset.data.condition as AssetCondition,
                    expectedReturnOn: '', purpose: '',
                  })
                }}
              >
                Hand it over
              </Button>
            )}
            {open && (
              <>
                <Button
                  type="button"
                  onClick={() => {
                    setError(null)
                    setReturnDraft({
                      id: open.id, returnedOn: todayLocal(),
                      conditionIn: '', note: '',
                    })
                  }}
                >
                  Take it back
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => act(
                    () => markLost.mutateAsync({ id: open.id, note: 'Reported lost' }),
                    'That could not be recorded')}
                >
                  Record it as lost
                </Button>
              </>
            )}
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setError(null)
                setMaintenanceDraft({
                  kind: 'SERVICE', scheduledOn: '', performedOn: '', nextDueOn: '',
                  vendor: '', cost: '', currency: asset.data.currency ?? 'AED',
                  description: '', tookOutOfService: false,
                })
              }}
            >
              Log maintenance
            </Button>
            {asset.data.status !== 'DISPOSED' && (
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setError(null)
                  setDisposeDraft({
                    disposedOn: todayLocal(),
                    method: 'SCRAPPED', proceeds: '', reason: '',
                  })
                }}
              >
                Dispose of it
              </Button>
            )}
          </div>
        }
      />

      {error && (
        <p className="rounded-md border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800">
          {error}
        </p>
      )}

      <div className="grid gap-3 lg:grid-cols-2">
        <Card className="space-y-2 p-4 text-sm">
          <div className="flex items-center justify-between">
            <h2 className="font-semibold text-[var(--color-heading)]">Where it stands</h2>
            <StatusBadge
              status={asset.data.status}
              tone={STATUS_TONE[asset.data.status as AssetStatus] ?? 'neutral'}
              label={ASSET_STATUS_LABELS[asset.data.status as AssetStatus] ?? asset.data.status}
            />
          </div>
          <p className="text-slate-600">
            Condition: {CONDITION_LABELS[asset.data.condition as AssetCondition]}
          </p>
          <p className="text-slate-600">
            {asset.data.heldByName
              ? <>Held by {asset.data.heldByName} since <DateValue value={asset.data.heldSince} /></>
              : 'Nobody is holding it.'}
          </p>
          {asset.data.warrantyExpiry && (
            <p className="text-slate-600">
              Warranty until <DateValue value={asset.data.warrantyExpiry} />
            </p>
          )}
        </Card>

        <Card className="space-y-2 p-4 text-sm">
          <h2 className="font-semibold text-[var(--color-heading)]">What it is worth</h2>
          {asset.data.bookValue == null ? (
            <p className="flex items-start gap-2 text-slate-600">
              <CircleAlert className="mt-0.5 size-4 shrink-0 text-amber-600" aria-hidden="true" />
              {asset.data.valuationNote}
            </p>
          ) : (
            <>
              <p className="text-lg font-semibold">
                <CurrencyValue
                  value={asset.data.bookValue}
                  currency={asset.data.currency ?? undefined}
                />
              </p>
              <p className="text-slate-600">{asset.data.valuationNote}</p>
            </>
          )}
          <p className="text-slate-600">
            Bought {asset.data.purchaseDate ? <DateValue value={asset.data.purchaseDate} /> : 'on an unrecorded date'}
            {asset.data.purchaseCost != null && (
              <> for <CurrencyValue value={asset.data.purchaseCost} currency={asset.data.currency ?? undefined} /></>
            )}
          </p>
          {asset.data.recoveryValue != null && (
            <p className="text-slate-600">
              Recovery value{' '}
              <CurrencyValue value={asset.data.recoveryValue} currency={asset.data.currency ?? undefined} />
              , used only where the settlement terms say so.
            </p>
          )}
        </Card>
      </div>

      <Card>
        <h2 className="border-b border-slate-100 p-4 font-semibold text-[var(--color-heading)]">
          Who has held it
        </h2>
        {assignments.isSuccess && assignments.data.length === 0 && (
          <EmptyState
            title="It has never been handed out"
            description="Every handover and return is kept, so an exit clearance can ask what somebody ever held."
          />
        )}
        {assignments.isSuccess && assignments.data.length > 0 && (
          <div className="overflow-x-auto overscroll-x-contain">
            <table className="w-full text-sm">
              <thead className="border-b border-slate-200 text-slate-600">
                <tr>
                  <th className="p-3 text-start font-medium">Person</th>
                  <th className="p-3 text-start font-medium">Out</th>
                  <th className="p-3 text-start font-medium">Back</th>
                  <th className="p-3 text-start font-medium">Condition</th>
                  <th className="p-3 text-start font-medium">Licence</th>
                  <th className="p-3 text-start font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {assignments.data.map((row) => (
                  <tr key={row.id} className="border-b border-slate-100 align-top">
                    <td className="p-3">{row.employeeName ?? row.employeeCode}</td>
                    <td className="p-3"><DateValue value={row.assignedOn} /></td>
                    <td className="p-3">
                      {row.returnedOn ? <DateValue value={row.returnedOn} /> : '—'}
                      {row.overdue && (
                        <span className="ms-1 text-xs font-medium text-amber-700">overdue</span>
                      )}
                    </td>
                    <td className="p-3 text-slate-600">
                      {CONDITION_LABELS[row.conditionOut as AssetCondition]}
                      {row.conditionIn && ` → ${CONDITION_LABELS[row.conditionIn as AssetCondition]}`}
                    </td>
                    <td className="p-3 max-w-xs text-xs text-slate-600">
                      {row.licenceNote ?? '—'}
                    </td>
                    <td className="p-3">
                      <StatusBadge
                        status={row.status}
                        tone={row.status === 'LOST' ? 'danger'
                          : row.status === 'ASSIGNED' ? 'info' : 'neutral'}
                        label={ASSIGNMENT_STATUS_LABELS[row.status as AssignmentStatus] ?? row.status}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card>
        <h2 className="border-b border-slate-100 p-4 font-semibold text-[var(--color-heading)]">Maintenance</h2>
        {maintenance.isSuccess && maintenance.data.length === 0 && (
          <EmptyState title="Nothing has been logged" description="Service, repairs and inspections go here with their cost." />
        )}
        {maintenance.isSuccess && maintenance.data.length > 0 && (
          <div className="overflow-x-auto overscroll-x-contain">
            <table className="w-full text-sm">
              <thead className="border-b border-slate-200 text-slate-600">
                <tr>
                  <th className="p-3 text-start font-medium">Kind</th>
                  <th className="p-3 text-start font-medium">Due</th>
                  <th className="p-3 text-start font-medium">Done</th>
                  <th className="p-3 text-start font-medium">Next</th>
                  <th className="p-3 text-start font-medium">Vendor</th>
                  <th className="p-3 text-end font-medium">Cost</th>
                </tr>
              </thead>
              <tbody>
                {maintenance.data.map((row) => (
                  <tr key={row.id} className="border-b border-slate-100">
                    <td className="p-3">
                      {MAINTENANCE_KIND_LABELS[row.kind as MaintenanceKind] ?? row.kind}
                    </td>
                    <td className="p-3">
                      {row.scheduledOn ? <DateValue value={row.scheduledOn} /> : '—'}
                      {row.overdue && (
                        <span className="ms-1 text-xs font-medium text-amber-700">overdue</span>
                      )}
                    </td>
                    <td className="p-3">
                      {row.performedOn ? <DateValue value={row.performedOn} /> : 'Not yet'}
                    </td>
                    <td className="p-3">
                      {row.nextDueOn ? <DateValue value={row.nextDueOn} /> : '—'}
                    </td>
                    <td className="p-3">{row.vendor ?? '—'}</td>
                    <td className="p-3 text-end">
                      <CurrencyValue value={row.cost} currency={row.currency ?? undefined} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <FormDialog
        open={assignDraft != null}
        onOpenChange={(open2) => { if (!open2) { setAssignDraft(null); setDialogError(null) } }}
        title="Hand it over"
        description="The licence check runs before anything is saved."
        submitLabel="Hand it over"
        loading={assign.isPending}
        error={dialogError}
        onSubmit={() => {
          if (!assignDraft || !id) return
          act(async () => {
            await assign.mutateAsync({
              id,
              body: {
                employeeId: assignDraft.employeeId,
                assignedOn: assignDraft.assignedOn,
                conditionOut: assignDraft.conditionOut,
                expectedReturnOn: assignDraft.expectedReturnOn || null,
                purpose: assignDraft.purpose || null,
              },
            })
            setAssignDraft(null)
          }, 'That asset could not be handed over', true)
        }}
      >
        {assignDraft && (
          <div className="space-y-3">
            <div>
              <Label htmlFor="assign-employee">Person</Label>
              <EmployeePicker
                  id="assign-employee"
                  value={assignDraft.employeeId}
                  onChange={(employeeId) => setAssignDraft({ ...assignDraft, employeeId: employeeId ?? '' })}
                />
            </div>
            {licence.isSuccess && licence.data.required && (
              <p className={`flex items-start gap-2 rounded-md border p-3 text-sm ${
                licence.data.satisfied
                  ? 'border-emerald-200 bg-emerald-50 text-emerald-900'
                  : 'border-amber-200 bg-amber-50 text-amber-900'
              }`}>
                <Info className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
                {licence.data.note}
                {!licence.data.satisfied && licence.data.enforcement === 'BLOCK'
                  && ' This category blocks the handover without it.'}
              </p>
            )}
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label htmlFor="assign-on">Handed over on</Label>
                <DatePicker
                  id="assign-on" value={assignDraft.assignedOn}
                  onChange={(event) =>
                    setAssignDraft({ ...assignDraft, assignedOn: event.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="assign-due">Expected back</Label>
                <DatePicker
                  id="assign-due" value={assignDraft.expectedReturnOn}
                  onChange={(event) =>
                    setAssignDraft({ ...assignDraft, expectedReturnOn: event.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="assign-condition">Condition going out</Label>
                <Select
                  id="assign-condition"
                  className="w-full"
                  value={assignDraft.conditionOut}
                  onChange={(event) => setAssignDraft({
                    ...assignDraft, conditionOut: event.target.value as AssetCondition })}
                >
                  {Object.entries(CONDITION_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </Select>
              </div>
              <div>
                <Label htmlFor="assign-purpose">What for</Label>
                <Input
                  id="assign-purpose" value={assignDraft.purpose}
                  onChange={(event) =>
                    setAssignDraft({ ...assignDraft, purpose: event.target.value })}
                />
              </div>
            </div>
          </div>
        )}
      </FormDialog>

      <FormDialog
        open={returnDraft != null}
        onOpenChange={(open2) => { if (!open2) { setReturnDraft(null); setDialogError(null) } }}
        title="Take it back"
        description="The condition it came back in is required."
        submitLabel="Take it back"
        loading={returnAsset.isPending}
        error={dialogError}
        onSubmit={() => {
          if (!returnDraft) return
          act(async () => {
            await returnAsset.mutateAsync({
              id: returnDraft.id,
              body: {
                returnedOn: returnDraft.returnedOn,
                conditionIn: returnDraft.conditionIn,
                note: returnDraft.note || null,
              },
            })
            setReturnDraft(null)
          }, 'That return could not be recorded', true)
        }}
      >
        {returnDraft && (
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label htmlFor="return-on">Came back on</Label>
              <DatePicker
                id="return-on" value={returnDraft.returnedOn}
                onChange={(event) =>
                  setReturnDraft({ ...returnDraft, returnedOn: event.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="return-condition">Condition</Label>
              <Select
                id="return-condition"
                className="w-full"
                value={returnDraft.conditionIn}
                onChange={(event) =>
                  setReturnDraft({ ...returnDraft, conditionIn: event.target.value })}
              >
                <option value="">Say what state it is in…</option>
                {Object.entries(CONDITION_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </Select>
              <p className="mt-1 text-xs text-slate-600">
                Fair wear and damage are the difference between a deduction and none.
              </p>
            </div>
            <div className="sm:col-span-2">
              <Label htmlFor="return-note">Note</Label>
              <Input
                id="return-note" value={returnDraft.note}
                onChange={(event) => setReturnDraft({ ...returnDraft, note: event.target.value })}
              />
            </div>
          </div>
        )}
      </FormDialog>

      <FormDialog
        open={maintenanceDraft != null}
        onOpenChange={(open2) => {
          if (!open2) { setMaintenanceDraft(null); setDialogError(null) }
        }}
        size="lg"
        title="Log maintenance"
        submitLabel="Save"
        loading={recordMaintenance.isPending}
        error={dialogError}
        onSubmit={() => {
          if (!maintenanceDraft || !id) return
          act(async () => {
            await recordMaintenance.mutateAsync({
              id,
              body: {
                kind: maintenanceDraft.kind,
                scheduledOn: maintenanceDraft.scheduledOn || null,
                performedOn: maintenanceDraft.performedOn || null,
                nextDueOn: maintenanceDraft.nextDueOn || null,
                vendor: maintenanceDraft.vendor || null,
                cost: maintenanceDraft.cost ? Number(maintenanceDraft.cost) : null,
                currency: maintenanceDraft.cost ? maintenanceDraft.currency : null,
                description: maintenanceDraft.description || null,
                tookOutOfService: maintenanceDraft.tookOutOfService,
              },
            })
            setMaintenanceDraft(null)
          }, 'That could not be logged', true)
        }}
      >
        {maintenanceDraft && (
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label htmlFor="maintenance-kind">Kind</Label>
              <Select
                id="maintenance-kind"
                className="w-full"
                value={maintenanceDraft.kind}
                onChange={(event) => setMaintenanceDraft({
                  ...maintenanceDraft, kind: event.target.value as MaintenanceKind })}
              >
                {Object.entries(MAINTENANCE_KIND_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </Select>
            </div>
            <div>
              <Label htmlFor="maintenance-vendor">Vendor</Label>
              <Input
                id="maintenance-vendor" value={maintenanceDraft.vendor}
                onChange={(event) =>
                  setMaintenanceDraft({ ...maintenanceDraft, vendor: event.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="maintenance-scheduled">Due on</Label>
              <DatePicker
                id="maintenance-scheduled" value={maintenanceDraft.scheduledOn}
                onChange={(event) =>
                  setMaintenanceDraft({ ...maintenanceDraft, scheduledOn: event.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="maintenance-performed">Done on</Label>
              <DatePicker
                id="maintenance-performed" value={maintenanceDraft.performedOn}
                onChange={(event) =>
                  setMaintenanceDraft({ ...maintenanceDraft, performedOn: event.target.value })}
              />
              <p className="mt-1 text-xs text-slate-600">
                One of these two is required: something is either planned or it happened.
              </p>
            </div>
            <div>
              <Label htmlFor="maintenance-next">Next due</Label>
              <DatePicker
                id="maintenance-next" value={maintenanceDraft.nextDueOn}
                onChange={(event) =>
                  setMaintenanceDraft({ ...maintenanceDraft, nextDueOn: event.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="maintenance-cost">Cost</Label>
              <Input
                id="maintenance-cost" type="number" value={maintenanceDraft.cost}
                onChange={(event) =>
                  setMaintenanceDraft({ ...maintenanceDraft, cost: event.target.value })}
              />
            </div>
          </div>
        )}
      </FormDialog>

      <FormDialog
        open={disposeDraft != null}
        onOpenChange={(open2) => { if (!open2) { setDisposeDraft(null); setDialogError(null) } }}
        title="Dispose of it"
        description="Once, and never while somebody is holding it."
        submitLabel="Dispose"
        loading={dispose.isPending}
        error={dialogError}
        onSubmit={() => {
          if (!disposeDraft || !id) return
          act(async () => {
            await dispose.mutateAsync({
              id,
              body: {
                disposedOn: disposeDraft.disposedOn,
                method: disposeDraft.method,
                proceeds: disposeDraft.proceeds ? Number(disposeDraft.proceeds) : null,
                currency: disposeDraft.proceeds ? asset.data.currency : null,
                reason: disposeDraft.reason || null,
              },
            })
            setDisposeDraft(null)
          }, 'That asset could not be disposed of', true)
        }}
      >
        {disposeDraft && (
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label htmlFor="dispose-on">Disposed on</Label>
              <DatePicker
                id="dispose-on" value={disposeDraft.disposedOn}
                onChange={(event) =>
                  setDisposeDraft({ ...disposeDraft, disposedOn: event.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="dispose-method">How</Label>
              <Select
                id="dispose-method"
                className="w-full"
                value={disposeDraft.method}
                onChange={(event) =>
                  setDisposeDraft({ ...disposeDraft, method: event.target.value })}
              >
                {Object.entries(DISPOSAL_METHOD_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </Select>
            </div>
            <div>
              <Label htmlFor="dispose-proceeds">Proceeds</Label>
              <Input
                id="dispose-proceeds" type="number" value={disposeDraft.proceeds}
                onChange={(event) =>
                  setDisposeDraft({ ...disposeDraft, proceeds: event.target.value })}
              />
              <p className="mt-1 text-xs text-slate-600">
                A gain or loss is only computed where the category has a depreciation method.
              </p>
            </div>
            <div>
              <Label htmlFor="dispose-reason">Why</Label>
              <Input
                id="dispose-reason" value={disposeDraft.reason}
                onChange={(event) =>
                  setDisposeDraft({ ...disposeDraft, reason: event.target.value })}
              />
            </div>
          </div>
        )}
      </FormDialog>
    </div>
  )
}
